import React, { useState, useCallback, useEffect } from "react";
import "./EvaluatePersona.css";
import { getTokenExp } from "@/Services/req_res_decode_encode";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

type OccupationType =
  | "Salaried"
  | "Business Owner"
  | "Self Employed"
  | "Professional"
  | "Homemaker"
  | "Retired";
type IncomeBandType = "low" | "mid" | "high" | "premium";
type MaritalStatusType = "Single" | "Married" | "Parent" | "Empty Nester";
type AgeGroupType = "18-30" | "31-40" | "41-50" | "51-60" | "60+";
type QuickPersonaType = "self" | "housewife" | "business" | "salaried";
type AgeRangeType =
  | "25-30"
  | "31-40"
  | "35-40"
  | "41-50"
  | "45-50"
  | "51-60"
  | "55+"
  | "25-30"
  | "35-40"
  | "45-50"
  | "55+";

interface AgeDetail {
  range: AgeRangeType;
  icon: string;
  title: string;
  pitch: string;
  uw: string;
}

interface FormData {
  age: AgeGroupType | "";
  gender: string;
  occupation: OccupationType | "";
  income: IncomeBandType | "";
  marital: MaritalStatusType | "";
}

// Static data for occupation-specific age groups
const selfEmployedAges: AgeDetail[] = [
  {
    range: "25-30",
    icon: "🚀",
    title: "Young Self Employed",
    pitch:
      "You are in the early phase of building your career and income. Cancer can suddenly stop your cash flow. This plan gives you lump sum for treatment + income support during recovery so your dreams stay on track.",
    uw: "Max Sum Assured up to ₹25 Lakh (subject to income proof)",
  },
  {
    range: "31-40",
    icon: "💼",
    title: "Growing Self Employed Professional",
    pitch:
      "Your income is growing but so are your responsibilities (home loan, family, children education). This plan protects your income during cancer treatment and recovery.",
    uw: "Max Sum Assured up to ₹30 Lakh (subject to income proof)",
  },
  {
    range: "41-50",
    icon: "🏢",
    title: "Established Self Employed",
    pitch:
      "You have built a stable income source. Protect it from health shocks. Cancer Care gives you financial backup so your business and family lifestyle remain secure.",
    uw: "Max Sum Assured up to ₹35 Lakh (subject to income proof)",
  },
  {
    range: "51-60",
    icon: "🛡️",
    title: "Senior Self Employed",
    pitch:
      "Protect the wealth you have created over years. This plan provides lump sum + flexible income benefit to safeguard your retirement and family.",
    uw: "Max Sum Assured up to ₹25 Lakh (subject to income proof)",
  },
];

const housewifeAges: AgeDetail[] = [
  {
    range: "25-30",
    icon: "👩‍❤️‍👨",
    title: "Young Housewife & Mother",
    pitch:
      "You are the heart of the family. Even though you may not earn, your role is priceless. This plan protects your family from financial burden if cancer strikes.",
    uw: "Max Sum Assured ₹15 Lakh",
  },
  {
    range: "35-40",
    icon: "🏠",
    title: "Mid-Age Housewife",
    pitch:
      "You manage home, children and everyone's needs. Cancer treatment can be very costly. This plan gives lump sum + income support during recovery.",
    uw: "Max Sum Assured ₹15 Lakh",
  },
  {
    range: "45-50",
    icon: "👵",
    title: "Experienced Homemaker",
    pitch:
      "Protect the savings you helped build over years. Get financial security up to ₹15 Lakh with flexible income option.",
    uw: "Max Sum Assured ₹15 Lakh",
  },
  {
    range: "55+",
    icon: "🛡️",
    title: "Senior Housewife",
    pitch:
      "Secure your health and retirement peace. Housewives are eligible for cancer cover up to ₹15 Lakh.",
    uw: "Max Sum Assured ₹15 Lakh",
  },
];

const businessOwnerAges: AgeDetail[] = [
  {
    range: "25-30",
    icon: "🚀",
    title: "Young Business Owner",
    pitch:
      "You are building your business. Cancer can suddenly stop your income. This plan gives you lump sum for treatment and income support so your business and dreams stay protected.",
    uw: "Individual policy only. Business/Keyman insurance not allowed.",
  },
  {
    range: "35-40",
    icon: "💼",
    title: "Growing Business Owner",
    pitch:
      "Your business is growing fast but so are your responsibilities. Cancer can pause income and affect business momentum. This plan creates a dedicated recovery fund.",
    uw: "Individual policy only. Business/Keyman insurance not allowed.",
  },
  {
    range: "45-50",
    icon: "🏢",
    title: "Established Business Owner",
    pitch:
      "You have built a successful business. Protect it from health shocks. Get lump sum + income benefit so your hard work and family lifestyle remain safe.",
    uw: "Individual policy only. Business/Keyman insurance not allowed.",
  },
  {
    range: "55+",
    icon: "🛡️",
    title: "Senior Business Owner",
    pitch:
      "Protect what you have built over decades. This plan helps safeguard your retirement corpus and family future against cancer.",
    uw: "Individual policy only. Business/Keyman insurance not allowed.",
  },
];

const salariedAges: AgeDetail[] = [
  {
    range: "25-30",
    icon: "📈",
    title: "Young Salaried Professional",
    pitch:
      "You are starting your career with big dreams and EMIs. Cancer can stop your salary. This plan gives you income during recovery so your career and lifestyle continue smoothly.",
    uw: "Strong financial eligibility based on income.",
  },
  {
    range: "35-40",
    icon: "👨‍👩‍👧",
    title: "Mid-Level Salaried",
    pitch:
      "You have home loan, children's education and family responsibilities. This plan protects your salary loss during cancer recovery with flexible income payout.",
    uw: "Strong financial eligibility based on income.",
  },
  {
    range: "45-50",
    icon: "🏦",
    title: "Senior Salaried Professional",
    pitch:
      "Protect your hard-earned lifestyle. Cancer treatment + recovery can impact savings. Get lump sum + income support to maintain financial stability.",
    uw: "Strong financial eligibility based on income.",
  },
  {
    range: "55+",
    icon: "🛡️",
    title: "Senior Salaried / Pre-Retirement",
    pitch:
      "Safeguard your retirement corpus. This plan provides long-term cancer protection and recovery income so you can retire peacefully.",
    uw: "Strong financial eligibility based on income.",
  },
];

const EvaluatePersona: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    age: "",
    gender: "",
    occupation: "",
    income: "",
    marital: "",
  });
  const [showQuickPlaceholder, setShowQuickPlaceholder] = useState(true);
  const [quickContent, setQuickContent] = useState<any | null>(null);
  const [showQuickResult, setShowQuickResult] = useState(false);
  const [quickPersonaInfo, setQuickPersonaInfo] = useState({ name: "", pitch: "" });
  const [persona, setLoadQuickPersona] = useState("");
  const [showFormResult, setShowFormResult] = useState(false);
  const [formResult, setFormResult] = useState({
    personaName: "",
    icon: "",
    why: "",
    pitch: "",
    points: [] as string[],
  });

  const [openAlert, setOpenAlert] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (getTokenExp()) {
      setOpenAlert(false);
    } else {
      setOpenAlert(true);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const wrap = document.querySelector(".wrap");
    if (wrap) wrap.scrollTop = 0;
  }, []);

  const Alters = () => {
    return (
      <>
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div
            className="bg-white rounded-xl p-6 shadow-xl w-[90%] max-w-md text-center"
            style={{ padding: 20 }}
          >
            <h2 className="text-red-600 text-xl font-bold mb-4"></h2>

            <p className="mb-6" style={{ marginTop: 10 }}>
              Your login is expired!
            </p>

            <button
              onClick={() => {
                localStorage.clear();
                setTimeout(() => {
                  window.location.href = import.meta.env.VITE_LOGINURL;
                }, 1000);
              }}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg"
              style={{ width: 50, marginTop: 20 }}
            >
              Ok
            </button>
          </div>
        </div>
      </>
    );
  };

  if (!openAlert) {
    return <Alters />;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const renderAgeCards = (ages: AgeDetail[], onSelect: (range: AgeRangeType) => void) => (
    <div className="age-grid">
      {ages.map((age, idx) => (
        <div key={idx} className="age-card" onClick={() => onSelect(age.range)}>
          <div className="age-icon">{age.icon}</div>
          <strong>
            {age.title} ({age.range})
          </strong>
          <p className="age-pitch">{age.pitch}</p>
          <div className="uw-note">
            <strong>UW Note:</strong> {age.uw}
          </div>
        </div>
      ))}
    </div>
  );

  const showSelfEmployedAges = () => {
    setShowQuickPlaceholder(true);
    setShowQuickResult(false);
    setQuickContent(
      <>
        <h3 className="age-section-title">Self Employed - Choose Age Group</h3>
        {renderAgeCards(selfEmployedAges, (range) => loadAgeSpecific("self", range))}
      </>,
    );
  };

  const showHousewifeAges = () => {
    setShowQuickPlaceholder(true);
    setShowQuickResult(false);
    setQuickContent(
      <>
        <h3 className="age-section-title">Housewife - Choose Age Group</h3>
        {renderAgeCards(housewifeAges, (range) => loadAgeSpecific("housewife", range))}
      </>,
    );
  };

  const showBusinessOwnerAges = () => {
    setShowQuickPlaceholder(true);
    setShowQuickResult(false);
    setQuickContent(
      <>
        <h3 className="age-section-title">Business Owner - Choose Age Group</h3>
        {renderAgeCards(businessOwnerAges, (range) => loadAgeSpecific("business", range))}
      </>,
    );
  };

  const showSalariedAges = () => {
    setShowQuickPlaceholder(true);
    setShowQuickResult(false);
    setQuickContent(
      <>
        <h3 className="age-section-title">Salaried Professional - Choose Age Group</h3>
        {renderAgeCards(salariedAges, (range) => loadAgeSpecific("salaried", range))}
      </>,
    );
  };

  const loadQuickPersona = (type: QuickPersonaType) => {
    setShowFormResult(false);
    switch (type) {
      case "self":
        showSelfEmployedAges();
        break;
      case "housewife":
        showHousewifeAges();
        break;
      case "business":
        showBusinessOwnerAges();
        break;
      case "salaried":
        showSalariedAges();
        break;
      default:
        break;
    }
  };

  const loadAgeSpecific = (type: string, range: AgeRangeType) => {
    setShowQuickPlaceholder(false);
    setShowQuickResult(true);
    setQuickPersonaInfo({
      name: `${type.toUpperCase()} (${range})`,
      pitch: `Recommended pitch for ${type} customer in age band ${range}.`,
    });
  };

  const evaluatePersona = () => {
    try {
      if (getTokenExp()) {
        localStorage.clear();
        toast.error("Your login is expired!");
        setTimeout(() => {
          window.location.href = import.meta.env.VITE_LOGINURL;
        }, 2000);
        return false;
      }
      const { age, gender, occupation, marital } = formData;
      if (!age || !gender || !occupation) {
        alert("Please fill Age, Gender & Occupation");
        return;
      }
      let personaName = "";
      let icon = "";
      let why = "";
      let pitch = "";
      let points: string[] = [];
      if (occupation === "Business Owner") {
        personaName = "Business Owner";
        icon = "💼";
        why = "Self-employed with direct link between health and income.";
        pitch =
          "Sir, as a business owner your health is directly linked to your income. If you are down with cancer, your business can suffer. This plan gives you financial backup so you can focus on recovery without worrying about money.";
        points = [
          "Business continuity",
          "Income protection",
          "Treatment support",
          "Return of Premium option",
        ];
      } else if (gender === "Female" && (occupation === "Homemaker" || marital === "Parent")) {
        personaName = "Family Caregiver";
        icon = "👩‍❤️‍👨";
        why = "Homemaker / Mother – priceless role in family.";
        pitch =
          "Even though you may not be earning, your role in the family is priceless. This plan helps avoid financial burden during expensive cancer treatment.";
        points = ["Family protection", "Peace of mind", "Support during treatment"];
      } else if (age === "18-30" || age === "31-40") {
        personaName = "Young Achiever";
        icon = "🚀";
        why = "Young professional building career and future.";
        pitch =
          "Buying young means lower premium and long-term protection against rising cancer costs.";
        points = ["Lower premium", "Long-term protection", "Return of Premium"];
      } else {
        personaName = "Prudent Planner";
        icon = "🧠";
        why = "Balanced profile seeking strong protection.";
        pitch =
          "Protect your future lifestyle and savings from the growing financial impact of cancer treatment.";
        points = ["Comprehensive cover", "Flexible benefits", "Preventive care support"];
      }
      setFormResult({ personaName, icon, why, pitch, points });
      setShowFormResult(true);
    } catch (error) {
      console.error();
    }
  };

  return (
    <div
      className="evaluate-persona"
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      style={{ userSelect: "none" }}
    >
      <div className="header">
        <img
          src="https://www.freelogovectors.net/wp-content/uploads/2021/01/tata-aia-life-logo-freelogovectors.net_.png"
          alt="TATA AIA Logo"
        />
        <div className="header-actions">
          <select className="lang-select" defaultValue="🌐 Choose Language">
            <option value="🌐 Choose Language">🌐 Choose Language</option>
            <option value="Hindi">Hindi</option>
            <option value="Marathi">Marathi</option>
            <option value="Tamil">Tamil</option>
            <option value="Gujarati">Gujarati</option>
            <option value="Telugu">Telugu</option>
            <option value="Bengali">Bengali</option>
            <option value="Kannada">Kannada</option>
          </select>
        </div>
      </div>
      <div className="cc-page-new-persona">
        <div className="page">
          <div className="content-area">
            <div className="hero-compact">
              <div className="hero-pill">PERSONA MATCHER</div>
              <h1 className="hero-title">Evaluate the Persona</h1>
              <p className="hero-subtitle">
                Identify the right customer profile and unlock the most effective sales pitch for
                Cancer Care.
              </p>
            </div>
            <div
              className="noPrint"
              style={{ margin: "12px 0 12px 0", display: "flex", alignItems: "center" }}
            >
              <button
                className="topbar-change text-white bg-[#c8102e] border border-[#c8102e]  rounded-xl font-bold text-sm"
                style={{
                  paddingTop: "12px",
                  paddingBottom: "12px",
                  paddingLeft: "25px",
                  paddingRight: "30px",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/")}
              >
                ← Back
              </button>
            </div>
            {/* QUICK ACCESS */}
            <div className="quick-persona-section">
              <h2 className="quick-title">⚡ Quick Access Personas</h2>
              <div className="quick-group">
                <h3>Occupation Based</h3>
                <div className="quick-grid">
                  <button
                    className={`quick-tile ${persona === "self" ? "active-card" : ""}`}
                    onClick={() => {
                      setLoadQuickPersona("self");
                      loadQuickPersona("self");
                    }}
                  >
                    👨‍💼 Self Employed
                  </button>
                  <button
                    className={`quick-tile ${persona === "housewife" ? "active-card" : ""}`}
                    onClick={() => {
                      setLoadQuickPersona("housewife");
                      loadQuickPersona("housewife");
                    }}
                  >
                    👩 House Wife
                  </button>
                  <button
                    className={`quick-tile ${persona === "business" ? "active-card" : ""}`}
                    onClick={() => {
                      setLoadQuickPersona("business");
                      loadQuickPersona("business");
                    }}
                  >
                    💼 Business Owner
                  </button>
                  <button
                    className={`quick-tile ${persona === "salaried" ? "active-card" : ""}`}
                    onClick={() => {
                      setLoadQuickPersona("salaried");
                      loadQuickPersona("salaried");
                    }}
                  >
                    🏢 Salaried
                  </button>
                </div>
              </div>
            </div>
            {/* QUICK PERSONA RESPONSE */}
            <div className="quick-response-area">
              {showQuickPlaceholder && quickContent && (
                <div className="quick-placeholder">{quickContent}</div>
              )}
              {!showQuickPlaceholder && showQuickResult && (
                <div className="quick-result">
                  <div className="result-card">
                    <h3 className="result-subtitle">QUICK PERSONA MATCH</h3>
                    <div className="persona-name">{quickPersonaInfo.name}</div>
                    <div className="persona-icon">🎯</div>
                    <div className="pitch-container">
                      <strong>Recommended Pitch</strong>

                      <span>{quickPersonaInfo.pitch}</span>
                    </div>
                  </div>
                </div>
              )}
              {showQuickPlaceholder && !quickContent && (
                <div className="quick-placeholder">
                  <p className="placeholder-text" style={{ textAlign: "center" }}>
                    Click a Quick Persona to see recommendations
                  </p>
                </div>
              )}
            </div>
            {/* DETAILED FORM */}
            <h2 className="section-title">📋 Detailed Customer Analysis</h2>
            <div className="persona-input-card">
              <h2>Enter Customer Details</h2>

              <div className="field">
                <label>Age Group</label>
                <select
                  id="p-age"
                  className="input"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                >
                  <option value="">Select Age Group</option>
                  <option value="18-30">18–30 Years</option>
                  <option value="31-40">31–40 Years</option>
                  <option value="41-50">41–50 Years</option>
                  <option value="51-60">51–60 Years</option>
                  <option value="60+">60+ Years</option>
                </select>
              </div>
              <div className="field">
                <label>Gender</label>
                <select
                  id="p-gender"
                  name="gender"
                  className="input"
                  value={formData.gender}
                  onChange={handleInputChange}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div className="field">
                <label>Occupation</label>
                <select
                  id="p-occ"
                  name="occupation"
                  className="input"
                  value={formData.occupation}
                  onChange={handleInputChange}
                >
                  <option value="">Select Occupation</option>
                  <option value="Salaried">Salaried</option>
                  <option value="Business Owner">Business Owner</option>
                  <option value="Self Employed">Self Employed</option>
                  <option value="Professional">Professional</option>
                  <option value="Homemaker">Homemaker</option>
                  <option value="Retired">Retired</option>
                </select>
              </div>
              <div className="field">
                <label>Annual Income</label>
                <select
                  id="p-inc"
                  name="income"
                  className="input"
                  value={formData.income}
                  onChange={handleInputChange}
                >
                  <option value="">Select Income Band</option>
                  <option value="low">&lt; 5 Lacs</option>
                  <option value="mid">5 – 10 Lacs</option>
                  <option value="high">10 – 25 Lacs</option>
                  <option value="premium">25 Lacs +</option>
                </select>
              </div>
              <div className="field">
                <label>Marital Status</label>
                <select
                  id="p-marital"
                  name="marital"
                  className="input"
                  value={formData.marital}
                  onChange={handleInputChange}
                >
                  <option value="">Select</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Parent">Married with Children</option>
                  <option value="Empty Nester">Empty Nester</option>
                </select>
              </div>
              <button onClick={evaluatePersona} className="btn btn-purple">
                EVALUATE PERSONA &amp; GET PITCH
              </button>
            </div>
          </div>
        </div>
        {/* FORM RESPONSE */}
        {showFormResult && (
          <div className="cc-page-new-persona-results">
            <div className="result-card">
              <h3 className="result-subtitle">PERSONA DETECTED</h3>
              <div className="persona-name">{formResult.personaName}</div>
              <div className="persona-icon">{formResult.icon}</div>
              <div className="pitch-container">
                <strong>Why this Persona?</strong>

                <span>{formResult.why}</span>
              </div>
              <strong>Recommended Pitch</strong>
              <div className="pitch-text">{formResult.pitch}</div>
              <ul className="key-points">
                {formResult.points.map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EvaluatePersona;
